import { Form, Input, Select } from "antd";
import { FormInstance } from "antd/es/form";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from "react-redux";
import Footer from "../../../components/Popup/_components/Footer";
import { callEventPopup } from "../../../contexts/PopupContext";
import { UserService } from "../../../util/services/user";
import { AppDispatch, useSelector } from "../../../util/store/store";
import { ACTION_TYPE, USER_ROLE, userRole } from "../../../util/varibles/constants";
import { filterOption, showErrorResponse } from "../../../util/varibles/global";
import { IFactory, ISite } from "../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../util/varibles/message";
import { ACTION } from "../../../util/varibles/permission";
import { formValidation } from "../../../util/varibles/validation";
import { IUser } from "../../LoginPage/constants";
import { userActions } from "../reducer";
import styles from './style.module.scss';

const rulesOfForm = {
    email: [formValidation.require('Email')],
    tenant_id: [formValidation.require('Customer')],
    password: [formValidation.require('Password')],
    confirm_password: [
        formValidation.require('Confirm password'),
        formValidation.confirmPassword()
    ],
    regions: [formValidation.require('Regions')],
    roles: [formValidation.require('Roles')]
}

const renderOptions = (list: any) => {
    return list.map((item: any) => ({ value: item.id, label: item.name })) || []
}

const renderOption = (item: any) => {
    return { value: item.id, label: item.name }
}

const haveRolesChanged = (prev: any, current: any) => {
    const previousRoles = prev.roles || [];
    const currentRoles = current.roles || [];

    return previousRoles.length !== currentRoles.length
        || previousRoles.some((role: USER_ROLE, index: number) => role !== currentRoles[index]);
}

const getSitesAndFactories = (props: {
    sites: ISite[],
    factories: IFactory[]
}, currentTenantId: string, isSuperAdmin: boolean) => {
    const { sites, factories } = props;
    return {
        sites: isSuperAdmin
            ? sites.reduce((list: any, item) => item.tenant_id === currentTenantId ? [...list, renderOption(item)] : list, [])
            : renderOptions(sites),
        factories: isSuperAdmin
            ? factories.reduce((list: any, item) => item.tenant_id === currentTenantId ? [...list, renderOption(item)] : list, [])
            : renderOptions(factories)
    }
}

interface IProps {
    user?: IUser
    editMode: ACTION_TYPE
}

const PopupEditUser: React.FC<IProps> = (props) => {
    const dispatch = useDispatch<AppDispatch>();
    const userTenantId = useSelector(state => state.login.user.tenant_id);
    const permission = useSelector(state => state.login.user.permission);
    const sites = useSelector(state => state.user.sites);
    const factories = useSelector(state => state.user.factories);
    const customers = useSelector(state => state.user.customers);
    const loadingSites = useSelector(state => state.user.loadingSites);
    const loadingFactories = useSelector(state => state.user.loadingFactories);
    const formRef = useRef<FormInstance>(null);
    const [loading, setLoading] = useState(false);
    const [source, setSource] = useState({
        vessels: [],
        regions: [],
        sites: [],
        factories: [],
    });
    const onClose = useRef(callEventPopup().onClose);

    const { editMode, user = { email: '', tenant_id: null } } = props
    const isSuperAdmin = permission[ACTION.TENANT.CREATE];
    const roleOptions = useMemo(() => {
        const roles = Object.entries(userRole)
            .filter(([key]) => isSuperAdmin || key !== USER_ROLE.SUPER_ADMIN);

        return roles.map(([value, role]) => ({ value, label: role.name }));
    }, [isSuperAdmin]);
    const customerOptions = useMemo(() => customers.map(item => ({
        value: item.tenant_id,
        label: item.name
    })), [customers]);
    const { tenant_id: itemTenantId } = user;

    const handleChangeTenant = useCallback((currentTenantId: any) => {
        let vessels = [], regions = [];
        const sitesFactories = getSitesAndFactories({ sites, factories }, currentTenantId, isSuperAdmin);
        const customer = customers.find((item: any) => item.tenant_id === currentTenantId)

        if (customer) {
            vessels = renderOptions(customer.vessels || []);
            regions = renderOptions(customer.regions || []);
        }

        setSource({ vessels, regions, ...sitesFactories });
    }, [customers, factories, isSuperAdmin, sites])

    useEffect(() => {
        if (!isSuperAdmin)
            handleChangeTenant(userTenantId);
        else if (editMode === ACTION_TYPE.EDIT) {
            handleChangeTenant(itemTenantId);
        }
    }, [editMode, isSuperAdmin, userTenantId, itemTenantId, handleChangeTenant])

    const onSelectRole = useCallback((value: USER_ROLE) => {
        const currentValue: USER_ROLE[] = formRef.current?.getFieldValue("roles");
        const isReadOnly = [...currentValue, value].some(item => item === USER_ROLE.READ_ONLY);
        if (isReadOnly) {
            const newValue = value === USER_ROLE.READ_ONLY ? [value] : currentValue.filter(item => item !== USER_ROLE.READ_ONLY);
            formRef.current?.setFieldsValue({ roles: newValue });
        }
    }, []);

    const handleSubmit = (values: any) => {
        const {
            name = '-',
            profile = '-',
            vessels = [],
            regions = [],
            email,
            sites = [],
            factories = [],
            tenant_id,
            ...rest
        } = values

        const user = {
            ...rest,
            name,
            profile,
            email: (email || '').replace(' ', ''),
            vessels,
            regions,
            tenant_id: String(tenant_id),
            sites,
            factories
        }

        if (!isSuperAdmin) {
            user.tenant_id = userTenantId;
        }

        setLoading(true);
        if (editMode === ACTION_TYPE.EDIT) {
            const { user_id, username, email } = props.user || {};
            user.username = username;
            user.user_id = user_id;
            user.email = email;
            UserService.update(user)
                .then((rs: any) => {
                    setLoading(false);
                    notify.success(NotifyCode.S2)([user.email]);
                    dispatch(userActions.updateUser(rs));
                })
                .catch(error => {
                    setLoading(false);
                    showErrorResponse('Update failed', error);
                })
        } else {
            delete user.confirm;
            setLoading(true);
            UserService.create(user)
                .then((rs: any) => {
                    setLoading(false);
                    notify.success(NotifyCode.S1)([user.email]);
                    dispatch(userActions.updateUser(rs));
                    onClose.current();
                })
                .catch(error => {
                    setLoading(false);
                    showErrorResponse('Create failed', error);
                })
        }
    }

    return <Form className={styles["popup-edit-user"]} ref={formRef} initialValues={user} onFinish={handleSubmit} layout='vertical'>
        {editMode === ACTION_TYPE.CREATE
            ? <>
                <Form.Item
                    label={<span className='text-[15px]'>Email</span>}
                    name="email"
                    rules={rulesOfForm.email}
                >
                    <Input className='h-[36px]' placeholder="Email" />
                </Form.Item>
                <Form.Item
                    label={<span className='text-[15px]'>Password</span>}
                    name="password"
                    rules={rulesOfForm.password}
                >
                    <Input.Password className='h-[36px]' placeholder="Password" autoComplete='new-password' />
                </Form.Item>
                <Form.Item
                    name="confirm"
                    label="Confirm Password"
                    dependencies={['password']}
                    hasFeedback
                    rules={rulesOfForm.confirm_password}
                >
                    <Input.Password className='h-[36px]' autoComplete='new-password' placeholder="Confirm Password" />
                </Form.Item>
            </>
            : <div className={styles['line-info']}>
                <label>Email</label>
                <div>{user.email}</div>
            </div>}

        {isSuperAdmin && <Form.Item
            label={<span className='text-[15px]'>Customer</span>}
            name="tenant_id"
            rules={rulesOfForm.tenant_id}
        >
            <Select
                onChange={handleChangeTenant}
                className='select-single'
                placeholder='Customer'
                showSearch={{ filterOption }}
                options={customerOptions}
            />
        </Form.Item>}

        <Form.Item label={<span className='text-[15px]'>Vessels</span>} name="vessels"
        >
            <Select
                className='select-mode-tag'
                mode="multiple"
                tokenSeparators={[',']}
                placeholder='Vessels'
                showSearch={{ filterOption }}
                options={source.vessels}
            />
        </Form.Item>
        <Form.Item
            label={<span className='text-[15px]'>Regions</span>}
            name="regions"
            rules={rulesOfForm.regions}
        >
            <Select
                className='select-mode-tag'
                mode="multiple"
                tokenSeparators={[',']}
                placeholder='Regions'
                showSearch={{ filterOption }}
                options={source.regions}
            />
        </Form.Item>
        <Form.Item
            label={<span className='text-[15px]'>Roles</span>}
            name="roles"
            rules={rulesOfForm.roles}
        >
            <Select
                className='select-mode-tag'
                mode="multiple"
                tokenSeparators={[',']}
                placeholder='Roles'
                onSelect={onSelectRole}
                showSearch={{ filterOption }}
                options={roleOptions}
            />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={haveRolesChanged}>
            {({ getFieldValue }) => {
                const roles = getFieldValue('roles') || [];
                const isSite = roles.includes(USER_ROLE.SITE_MANAGER);
                const isFactory = roles.includes(USER_ROLE.FACTORY_MANAGER);

                return <>
                    {isSite && <Form.Item
                        label={<span className='text-[15px]'>Sites</span>}
                        name='sites'
                    >
                        <Select
                            className='select-mode-tag'
                            mode="multiple"
                            tokenSeparators={[',']}
                            placeholder='Sites'
                            showSearch={{ filterOption }}
                            loading={loadingSites}
                            options={source.sites}
                        />
                    </Form.Item>}
                    {isFactory && <Form.Item
                        label={<span className='text-[15px]'>Factories</span>}
                        name='factories'
                    >
                        <Select
                            className='select-mode-tag'
                            mode="multiple"
                            tokenSeparators={[',']}
                            placeholder='Factories'
                            showSearch={{ filterOption }}
                            loading={loadingFactories}
                            options={source.factories}
                        />
                    </Form.Item>}
                </>
            }}
        </Form.Item>
        <Footer cancel={{ click: onClose.current }} ok={{ loading }} />
    </Form>
};

export default PopupEditUser;
